/* eslint-disable react/no-unescaped-entities */
import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/alrannan-admin-service.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/alrannan-administrative-services",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/goireland.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/goireland",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping.png",
        title: "Gallabox transforms Dubai Housekeeping's journey to service excellence on WhatsApp",
        sector: "Service",
        externalLink: "/case-study/dubai-housekeeping",
    },
];

const AlHilal: React.FC = () => {
    return (
        <NewLayout
            title="Al Hilal Houri Achieves 60% Growth | Gallabox Case Study"
            description="Discover how Al Hilal Houri Medical Equipment LLC transformed their customer service Gallabox’s WhatsApp chatbots, achieving 60% business growth."
            canonical="https://gallabox.com/case-study/alHilal"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/alHilal/alHilal-og.png" }}
        >
            <CaseStudyHerosection
                title="How Al Hilal Houri Medical Equipment streamlined support and achieved 60% growth with Gallabox"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/alHilal/alHilal-hero.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "60%", label: "increase in business growth" },
                    { percentage: "25%", label: "growth in first-time conversions" },
                    { percentage: "50%", label: "improvement in employee productivity" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Healthcare"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-shared-inbox", label: "Shared team inbox" },
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "WhatsApp Chatbot" },
                                    { link: "https://gallabox.com/whatsapp-integrations", label: "Integrations" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About Al Hilal Houri Medical Equipment">
                            <p className="mt-2">
                                Based in Abu Dhabi,{" "}
                                <a href="https://www.hourihearing.ae" className="text-blue-500" target="_blank" rel="noreferrer">
                                    Al Hilal Houri Medical Equipment LLC
                                </a>{" "}
                                is a premier provider of high-quality medical equipment and supplies across the UAE. Specializing in hearing aids, laboratory equipment, diagnostic
                                instruments, and medical consumables, they serve healthcare facilities with products sourced from reputed international manufacturers in Europe,
                                Japan, and the USA.
                            </p>
                            <p className="mt-5">
                                With over 43 years of experience in the hearing solutions segment alone, they've built their reputation on delivering products of the highest
                                standards while adhering to stringent quality and safety regulations.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"The challenge: When WhatsApp messages become overwhelming"} className="mt-0" />

                            <p className="mt-2">
                                As Meera Nicholas (NetSuite Administrator at Al Hilal Houri) explains, the medical equipment company faced a critical customer service bottleneck:
                                "We struggled to handle customers’ WhatsApp messages. We were not able to reply back to customers on time." For a medical equipment supplier where
                                timely responses can impact patient care, delayed communication isn’t something they could overlook.
                            </p>
                            <p className="mt-5">
                                The volume of WhatsApp inquiries had grown beyond what their team could manage effectively. Healthcare facilities needed quick responses about
                                equipment availability, specifications, and urgent orders. Individual patients required guidance on hearing aid selection and support. But with
                                messages flooding in across different team members' phones, important inquiries were getting lost or delayed.
                            </p>

                            <CaseStudyInformation subTitle={"From templates to frustration"} className="mt-10" />
                            <p className="mt-2">
                                Before Gallabox, Al Hilal Houri attempted to manage the chaos with WhatsApp Business's basic features. "We had the business account, where there
                                were short message templates," Meera recalls. While templates reduced typing time for common responses, they couldn't address the core problem.
                            </p>

                            <p className="mt-5">
                                "It still didn't help to answer them properly where human intervention was required," she explains. Medical equipment inquiries often need detailed,
                                personalized responses – something that basic templates couldn't provide. The team was stuck between giving quick but inadequate responses or taking
                                too long to provide proper information.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Choosing Gallabox over AiSensy and Respond"} className="mt-0" />
                            <p className="mt-2">
                                Al Hilal Houri evaluated both AiSensy and Respond before choosing Gallabox. The decision came down to three crucial factors: "Feature parity,
                                pricing, and more importantly a hands on support and success team”, as Meera puts it succinctly.
                            </p>
                            <p className="mt-5">
                                In the medical equipment industry, where margins can be tight and customer service is paramount, finding a solution that balanced cost-effectiveness
                                with functionality was essential. Gallabox's responsive customer support also resonated with a company that understood the importance of timely
                                assistance.
                            </p>

                            <CaseStudyInformation subTitle={"Implementation: Chatbots to automate the busywork of customer support"} className="mt-10" />
                            <p className="mt-2">
                                Al Hilal Houri kept their implementation focused, deploying Gallabox with two primary objectives: streamline customer communication and automate
                                customer support. They leveraged chatbots as their primary feature.
                            </p>
                            <p className="mt-5">
                                Chatbots now handle initial inquiries about medical equipment, directing customers to the right product categories, providing basic specifications,
                                and capturing detailed requirements for complex orders. The bot can differentiate between urgent medical facility orders and individual patient
                                inquiries, routing each appropriately.
                            </p>
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"The results: Healthy growth metrics"} className="mt-0" />
                            <p className="mt-2">The impact of Gallabox on Al Hilal Houri's business has been substantial. </p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "Business growth improved by 60%: The company has transformed its customer service from a bottleneck to a competitive advantage.",
                                    },
                                    {
                                        listItem:
                                            "New conversion rates grew 25%: Quick, accurate responses to initial inquiries mean more healthcare facilities and patients choose Al Hilal Houri for their medical equipment needs.",
                                    },
                                    {
                                        listItem:
                                            "Employee productivity increased 50%: This improvement is significant given that staff can now focus on complex medical consultations rather than repetitive inquiries.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <CaseStudyInformation subTitle={"The scaling rewards of responsive service"} className="mt-10" />
                            <p className="mt-2">
                                In the medical equipment industry, trust is everything. Healthcare facilities need suppliers who respond quickly to urgent needs. Patients seeking
                                hearing aids or other medical devices want knowledgeable support throughout their journey. The 25% improvement in first-time conversions shows how
                                automated yet personalized responses build this trust from the first interaction.
                            </p>
                            <p className="mt-5">
                                With chatbots handling routine inquiries about product availability, specifications, and order status, the team can dedicate more time to complex
                                consultations. This is particularly valuable for their hearing aid division, where patients often need detailed guidance on selecting the right
                                solution for their specific needs.
                            </p>
                            <p className="mt-5">
                                As the UAE's healthcare sector continues to grow and modernize, Al Hilal Houri's investment in communication infrastructure positions them well for
                                expansion. Their partnership with international manufacturers requires efficient communication to manage orders and specifications. Gallabox ensures
                                nothing gets lost in translation – or in a flood of WhatsApp messages.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCardsDetails?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default AlHilal;
